'use client';

/* eslint-disable tailwindcss/no-arbitrary-value, tailwindcss/no-unnecessary-arbitrary-value -- CMS-1623 follows fixed Figma modal widths, shadows and brand colors. */
/* eslint-disable tailwindcss/no-custom-classname */
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import {
  ATTR_NAMES,
  getAttrFromProduct,
} from '@hyphengroup/cms-components/types/attrNames';
import classNames from 'classnames';
import {
  FormEvent,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import Log from 'utils/logging.client';
import {
  getDefaultMarketingConsent,
  getEmailValidationMessage,
} from '../lib/footer';
import {
  IDENTITY_SWITCH_LOGOUT_FAILED_LOG,
  readLoginStatus,
  useApplyLoginStatus,
} from '../lib/loginStatus';
import {
  ApplyV3LoginMethod,
  trackApplyV3ApplyNow,
  trackApplyV3ConsentInteraction,
  trackApplyV3EmailFocus,
} from '../lib/tracking';
import { RewardStickyFooterText } from '$/(application)/components/i18n';
import { ApplyStaticText } from '$/(cms)/-cms/apply-v3/[[...slugs]]/i18n';
import { ApplyViewMode } from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/types';
import type { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import type { ApplyMode } from '$/(cms)/_modules/routing';
import Button from '$/components/Button';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';
import AlphaCheckbox from '$/modules/material-tailwind/AlphaCheckbox';
import { AlphaTextInput } from '$/modules/material-tailwind/AlphaTextInput';

export type ApplyEmailFooterSubmitPayload = {
  email: string;
  marketingConsent: boolean;
  loginMethod: ApplyV3LoginMethod;
};

/** What the reader has entered so far; outlives a remount of the footer. */
export type ApplyEmailDraft = { email?: string; marketingConsent?: boolean };

type Notice = 'session-ended' | 'identity-switch-failed';

// The placeholder reserves the anonymous CTA height: that is the variant the
// cached HTML paints and the one almost every visitor resolves to.
const PENDING_CTA_HEIGHT = 'min-h-[88px] md:min-h-11';

// The signed-in CTA stacks its identity-switch link under the button until lg,
// so it is taller than the anonymous row in that band.
const IDENTIFIED_CTA_HEIGHT = 'min-h-[88px] md:min-h-[92px] lg:min-h-11';

type Props = {
  mode: ApplyMode;
  viewMode: ApplyViewMode;
  product: CmsApplyRewardProduct;
  onSubmit?: (payload: ApplyEmailFooterSubmitPayload) => void;
  draft?: MutableRefObject<ApplyEmailDraft>;
};

export function ApplyEmailFooter(props: Props) {
  const context = useClientContext();
  const { authEndpoint } = context;
  const staticText = getClientTranslations(ApplyStaticText, context);
  const rewardStickyFooterText = getClientTranslations(
    RewardStickyFooterText,
    context,
  );
  const [marketingConsent, setMarketingConsent] = useState(
    () =>
      props.draft?.current.marketingConsent ??
      getDefaultMarketingConsent(context.brand),
  );
  const [loginStatus, setLoginStatus] = useApplyLoginStatus(props.mode);
  const [notice, setNotice] = useState<Notice>();
  const [switchingIdentity, setSwitchingIdentity] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const focusEmailOnSwitch = useRef(false);
  useEffect(() => {
    if (loginStatus.state === 'anonymous' && focusEmailOnSwitch.current) {
      focusEmailOnSwitch.current = false;
      emailInputRef.current?.focus();
    }
  }, [loginStatus]);

  // Per-product MARKETING_CONSENT (rich text) overrides the hardcoded expanded
  // consent copy when present; otherwise fall back to the static i18n text.
  const consentAttr = getAttrFromProduct(
    ATTR_NAMES.MARKETING_CONSENT,
    props.product.catalogueProduct ?? undefined,
  );
  const consentRichText = consentAttr?.value?.trim() ? consentAttr.value : null;

  const submitLead = (
    formData: FormData,
    email: string,
    identifiedEmail?: string,
  ) => {
    const nextMarketingConsent = formData.get('marketingConsent') === 'on';
    const loginMethod = getSubmissionMethod(identifiedEmail);
    trackApplyV3ApplyNow(
      props.product,
      email,
      nextMarketingConsent,
      loginMethod,
    );
    props.onSubmit?.({
      email,
      marketingConsent: nextMarketingConsent,
      loginMethod,
    });
  };

  const onAnonymousSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    submitLead(formData, String(formData.get('email') ?? ''));
  };

  const onIdentifiedSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    // The session can lapse between paint and click, so the token is re-read
    // here rather than trusting the value the button was rendered from.
    const current = readLoginStatus(props.mode);
    if (current.state !== 'identified') {
      setLoginStatus(current);
      setNotice('session-ended');
      return;
    }
    submitLead(formData, current.email, current.email);
  };

  const onIdentitySwitch = async () => {
    setNotice(undefined);
    setSwitchingIdentity(true);
    try {
      const url = new URL(`${authEndpoint}/logout`, window.location.href);
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Logout responded with ${response.status}`);
      }
    } catch (error) {
      Log.error({
        message: IDENTITY_SWITCH_LOGOUT_FAILED_LOG,
        mode: props.mode,
        error,
      });
      setNotice('identity-switch-failed');
      setSwitchingIdentity(false);
      return;
    }
    focusEmailOnSwitch.current = true;
    setLoginStatus({ state: 'anonymous' });
    setSwitchingIdentity(false);
  };

  const handleMarketingConsentChange = (checked: boolean) => {
    trackApplyV3ConsentInteraction(props.product, checked);
    setMarketingConsent(checked);
    if (props.draft) props.draft.current.marketingConsent = checked;
  };

  const handleEmailFocus = () => {
    trackApplyV3EmailFocus(props.product);
  };

  const continueAsLabel =
    loginStatus.state === 'identified'
      ? rewardStickyFooterText.continueAsBtn.replace(
          '${userEmail}',
          () => loginStatus.email,
        )
      : '';

  return (
    <form
      className="flex w-full shrink-0 flex-col items-center gap-[--spacing-gap-y-sm]"
      data-component="apply-v3-email-footer"
      data-login-state={loginStatus.state}
      onSubmit={
        loginStatus.state === 'identified'
          ? onIdentifiedSubmit
          : onAnonymousSubmit
      }
    >
      {notice ? (
        <p
          className="mhg-alpha-typography-caption w-full text-left text-highlights/red-dark"
          role="status"
        >
          {notice === 'session-ended'
            ? staticText.sessionEnded
            : staticText.identitySwitchFailed}
        </p>
      ) : null}
      <div
        className={classNames(
          'grid w-full min-w-0 grid-cols-[minmax(0,1fr)] gap-2 md:gap-4',
          loginStatus.state === 'anonymous' &&
            'md:grid-cols-[minmax(0,1fr)_129px]',
        )}
      >
        {loginStatus.state === 'pending' ? (
          // Login status resolves only after hydration, so the CTA area is
          // held open until it lands.
          <div
            aria-hidden
            className={classNames('w-full', PENDING_CTA_HEIGHT)}
          />
        ) : loginStatus.state === 'identified' ? (
          <div
            className={classNames(
              'flex w-full min-w-0 flex-col items-center justify-center gap-2',
              IDENTIFIED_CTA_HEIGHT,
              // Page (M2/M3) desktop puts the CTA and the identity-switch link
              // on one row; the modal (M1) stays stacked at every width.
              props.viewMode === ApplyViewMode.Page &&
                'lg:flex-row lg:justify-between lg:gap-4',
            )}
          >
            {/* The account email is unbounded, so the button shrinks and
                ellipsises while `aria-label` keeps the full address. */}
            <Button
              aria-label={continueAsLabel}
              className={classNames(
                'w-full min-w-0 overflow-hidden',
                props.viewMode === ApplyViewMode.Page && 'lg:flex-1',
              )}
              disabled={switchingIdentity}
              styleType="cta"
              type="submit"
            >
              <span className="block min-w-0 truncate">{continueAsLabel}</span>
            </Button>
            {/* The stroke overrides the outline variant's own primary border;
                min-h-11 holds the 44px the reserved CTA heights are sized on,
                over the smaller `button/size-regular` (CMS-1941 design). */}
            <button
              className={classNames(
                'mhg-alpha-typography-body-2 flex min-h-11 w-full shrink-0 items-center justify-center py-2 underline text-text/link',
                'md:mhg-alpha-btn-outline-primary md:border-borders/default md:py-0 md:no-underline',
                props.viewMode === ApplyViewMode.Page && 'lg:w-auto lg:flex-1',
              )}
              disabled={switchingIdentity}
              onClick={onIdentitySwitch}
              type="button"
            >
              {staticText.useDifferentEmail}
            </button>
          </div>
        ) : (
          <>
            <div className="md:order-1">
              <AlphaTextInput
                aria-label={staticText.emailAddressLabel}
                autoComplete="email"
                className="!mhg-alpha-typography-button-1 !bg-white placeholder:text-text/disabled"
                containerProps={{
                  className: '!min-w-0 !h-11 !min-h-11',
                }}
                id="apply-v3-email"
                label=" "
                labelProps={{ className: 'hidden' }}
                name="email"
                defaultValue={props.draft?.current.email}
                onChange={(event) => {
                  if (props.draft) {
                    props.draft.current.email = event.currentTarget.value;
                  }
                }}
                onFocus={handleEmailFocus}
                onValidate={({ validity }) =>
                  getEmailValidationMessage(validity, staticText)
                }
                placeholder={staticText.emailPlaceholder}
                ref={emailInputRef}
                required
                type="email"
              />
            </div>
            {/* Below md the consent sits between the input and the button, so
                the button is ordered past it (it is the later sibling now). */}
            <div className="order-3 md:order-2">
              <Button
                className="w-full md:w-auto"
                styleType="cta"
                type="submit"
              >
                {staticText.applyNow}
              </Button>
            </div>
          </>
        )}
        <div
          className={classNames(
            // Only the anonymous layout has two columns; spanning two on the
            // single-column variants would create an implicit second one.
            loginStatus.state === 'anonymous' &&
              'order-2 md:order-3 md:col-span-2',
          )}
        >
          <MarketingConsent
            checked={marketingConsent}
            ariaLabel={staticText.marketingConsentAriaLabel}
            richText={consentRichText}
            consentExpandedLabel={rewardStickyFooterText.dialogMarketingConsent}
            consentLabel={rewardStickyFooterText.dialogMarketingConsentShort}
            seeLessLabel={staticText.seeLess}
            seeMoreLabel={staticText.seeMore}
            onChange={handleMarketingConsentChange}
          />
        </div>
      </div>
    </form>
  );
}

function getSubmissionMethod(userEmail?: string): ApplyV3LoginMethod {
  if (typeof window === 'undefined') return 'email';
  const action = new URLSearchParams(location.search ?? '').get('action');
  if (action === 'google') return 'google';
  return userEmail ? 'prelogin' : 'email';
}

function MarketingConsent(props: {
  ariaLabel: string;
  checked: boolean;
  // When the product has a MARKETING_CONSENT rich-text attribute, render it in
  // full (no see more/less). Otherwise fall back to the static short/full copy
  // with the see more/less toggle.
  richText?: string | null;
  consentLabel: ReactNode;
  consentExpandedLabel: ReactNode;
  seeLessLabel: string;
  seeMoreLabel: string;
  onChange: (checked: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex w-full items-start gap-2 text-[--text-secondary]">
      <AlphaCheckbox
        checked={props.checked}
        inputProps={{
          'aria-label': props.ariaLabel,
          name: 'marketingConsent',
          value: 'on',
        }}
        onChange={props.onChange}
      />
      {props.richText ? (
        // `baseTextStyle="none"` so the rich text inherits the caption
        // typography below instead of RichText's own body style.
        <RichText
          component="span"
          baseTextStyle="none"
          className="mhg-alpha-typography-caption pt-[2px]"
        >
          {props.richText}
        </RichText>
      ) : (
        <span className="mhg-alpha-typography-caption pt-[2px]">
          {expanded ? props.consentExpandedLabel : props.consentLabel}
          {!expanded ? '...' : ''}{' '}
          <button
            aria-expanded={expanded}
            className="underline text-inherit"
            type="button"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? props.seeLessLabel : props.seeMoreLabel}
          </button>
        </span>
      )}
    </div>
  );
}
